import React from 'react';
import { Link } from 'react-router-dom';
import { Globe, Webhook, Shield, CheckCircle, AlertTriangle } from 'lucide-react';
import { CodeBlock } from '../components/code/CodeBlock';

export const GatewayWebhook: React.FC = () => {
  const webhookRequestParams = [
    { name: 'transactionReference', type: 'string', required: true, description: 'Unique reference for the transaction generated by Nectar Gateway' },
    { name: 'merchantRef', type: 'string', required: true, description: 'Merchant\'s unique transaction reference.' },
    { name: 'grossAmount', type: 'decimal', required: true, description: 'Total transaction amount including fees.' },
    { name: 'requestedAmount', type: 'decimal', required: true, description: 'Original amount requested by the merchant.' },
    { name: 'transactionFee', type: 'decimal', required: true, description: 'Fee charged by Nectar Gateway for processing the transaction.' },
    { name: 'settlementAmount', type: 'decimal', required: true, description: 'Amount to be settled to the merchant after fees.' },
    { name: 'customerId', type: 'string', required: true, description: 'Unique customer identifier.' },
    { name: 'transactionDate', type: 'string', required: true, description: 'Date and time the transaction was completed (ISO 8601).' },
    { name: 'currencyCode', type: 'string', required: true, description: 'Currency of the transaction (e.g., NGN, USD).' },
    { name: 'callbackUrl', type: 'string', required: false, description: 'Merchant\'s callback URL where Nectar Gateway sends transaction updates.' },
    { name: 'status', type: 'integer', required: true, description: 'Transaction status code (see Status Codes section).' },
    { name: 'paymentRequestor', type: 'string', required: true, description: 'Source of payment request (e.g., WEB, MOBILE).' },
    { name: 'errorCode', type: 'string', required: false, description: 'Error code if the transaction failed.' },
    { name: 'errorMessage', type: 'string', required: false, description: 'Descriptive error message if any issue occurred.' },
    { name: 'externalRef', type: 'string', required: false, description: 'Reference used by external processors (if applicable).' },
    { name: 'paymentSource', type: 'string', required: true, description: 'Payment source used (e.g., CARD, BANK, OPay).' },
    { name: 'metadata', type: 'object', required: false, description: 'Custom data provided by the merchant (e.g., promo code, device type).' },
    { name: 'createdDate', type: 'string', required: true, description: 'Timestamp when the transaction record was created.' },
    { name: 'refundedAmount', type: 'decimal', required: false, description: 'Total amount refunded for the transaction.' },
    { name: 'lastRefundDate', type: 'string', required: false, description: 'Timestamp of the most recent refund event.' }
  ];

  const webhookRequestBodyJson = `{
  "transactionReference": "TXN-20250929-123456",
  "merchantRef": "MER-99887766",
  "grossAmount": 1500.00,
  "requestedAmount": 1500.00,
  "transactionFee": 25.50,
  "settlementAmount": 1474.50,
  "customerId": "CUS-55667788",
  "transactionDate": "2025-09-29T14:25:30",
  "currencyCode": "NGN",
  "callbackUrl": "https://merchant.example.com/callback",
  "status": 1,
  "paymentRequestor": "WEB",
  "errorCode": null,
  "errorMessage": null,
  "externalRef": "EXT-REF-12345",
  "paymentSource": "CARD",
  "metadata": {
    "deviceType": "MOBILE",
    "customerEmail": "customer@example.com",
    "promoCode": "WELCOME100"
  },
  "createdDate": "2025-09-29T14:20:00",
  "refundedAmount": 200.00,
  "lastRefundDate": "2025-09-29T15:00:00"
}`;

  const webhookResponseJson = `{
  "authentication": {
    "threeDS": {"status": "Y", "eci": "05"},
    "threeDS2": {"status": "N", "challengeIndicator": "04"},
    "amount": 100.50,
    "time": "2025-03-17T12:34:56Z",
    "transactionId": "txn_123456789",
    "version": "2.1.0"
  },
  "authorizationResponse": {
    "cardSecurityCodeError": "N",
    "commercialCardIndicator": "Y",
    "date": "2025-03-17",
    "financialNetworkCode": "VISA",
    "financialNetworkDate": "2025-03-17",
    "posData": "123456",
    "posEntryMode": "05",
    "processingCode": "00",
    "responseCode": "00",
    "stan": "123456",
    "time": "12:34:56",
    "transactionIdentifier": "456789123"
  },
  "device": {"browser": "Chrome"},
  "gatewayEntryPoint": "WEB",
  "merchant": "Merchant_001",
  "order": {
    "amount": 100.50,
    "authenticationStatus": "SUCCESS",
    "chargeback": {"amount": 0.00, "currency": "USD"},
    "creationTime": "2025-03-17T12:00:00Z",
    "currency": "USD",
    "id": "order_987654321",
    "lastUpdatedTime": "2025-03-17T12:45:00Z",
    "merchantAmount": 98.00,
    "merchantCategoryCode": "5411",
    "merchantCurrency": "USD",
    "notificationUrl": "https://merchant.com/webhook",
    "reference": "INV-20250317-001",
    "status": "COMPLETED",
    "totalAuthorizedAmount": 100.50,
    "totalCapturedAmount": 100.50,
    "totalDisbursedAmount": 98.00,
    "totalRefundedAmount": 0.00
  },
  "response": {
    "acquirerCode": "00",
    "acquirerMessage": "Approved",
    "cardSecurityCode": {"acquirerCode": "00", "gatewayCode": "APPROVED"},
    "gatewayCode": "APPROVED",
    "gatewayRecommendation": "PROCEED"
  },
  "result": "SUCCESS",
  "sourceOfFunds": {
    "provided": {
      "card": {
        "brand": "VISA",
        "expiry": {"month": "12", "year": "2027"},
        "fundingMethod": "CREDIT",
        "number": "411111******1111",
        "scheme": "VISA",
        "storedOnFile": "YES"
      }
    },
    "type": "CARD"
  },
  "timeOfLastUpdate": "2025-03-17T12:45:00Z",
  "timeOfRecord": "2025-03-17T12:34:56Z",
  "transaction": {
    "acquirer": {
      "batch": 12345,
      "date": "2025-03-17",
      "id": "acq_987654",
      "merchantId": "mid_456789",
      "settlementDate": "2025-03-18",
      "timeZone": "UTC",
      "transactionId": "txn_123456789"
    },
    "amount": 100.50,
    "authenticationStatus": "SUCCESS",
    "authorizationCode": "AUTH123",
    "currency": "USD",
    "id": "txn_123456789",
    "receipt": "RCPT-123456",
    "reference": "INV-20250317-001",
    "source": "WEB",
    "stan": "123456",
    "terminal": "TID12345",
    "type": "PURCHASE"
  },
  "version": "1.0"
}`;

  return (
    <div className="max-w-6xl mx-auto">
      <div className="mb-12">
        <h1 className="text-3xl font-bold text-white mb-4">Webhook</h1>
        
        {/* Introduction */}
        <div className="mb-8">
          <p className="text-white-muted mb-6 leading-relaxed">
            Nectar sends webhook notifications to you whenever there is a change in transaction status. 
            Webhooks help ensure your system stays synchronized without constant polling.
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-6">
              <div className="flex items-center mb-3">
                <Webhook className="h-5 w-5 text-blue-500 mr-2" />
                <h3 className="text-lg font-semibold text-blue-500">Real-time Updates</h3>
              </div>
              <ul className="text-sm text-blue-500/90 space-y-2">
                <li>• Instant payment notifications</li>
                <li>• Automatic status synchronization</li>
                <li>• Reliable event delivery</li>
                <li>• Retry mechanisms</li>
              </ul>
            </div>
            
            <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-6">
              <div className="flex items-center mb-3">
                <Shield className="h-5 w-5 text-green-500 mr-2" />
                <h3 className="text-lg font-semibold text-green-500">Security</h3>
              </div>
              <ul className="text-sm text-green-500/90 space-y-2">
                <li>• HTTPS-only delivery</li>
                <li>• Signature verification</li>
                <li>• IP whitelisting support</li>
                <li>• Encrypted payloads</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Webhook Request Body */}
        <div className="mb-8">
          <h2 className="text-xl font-semibold text-white mb-4">Webhook Request Body</h2>
          <CodeBlock code={webhookRequestBodyJson} language="json" />
          
          <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-6 mt-6">
            <div className="flex items-start">
              <AlertTriangle className="h-5 w-5 text-yellow-500 mr-3 mt-0.5 flex-shrink-0" />
              <div>
                <h3 className="text-lg font-semibold text-yellow-500 mb-2">Note</h3>
                <p className="text-sm text-yellow-500/90">
                  Fields such as <strong>errorCode</strong>, <strong>errorMessage</strong>, <strong>externalRef</strong>, 
                  <strong>metadata</strong>, and <strong>refundedAmount</strong> are optional.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Webhook Request Parameters */}
        <div className="mb-8">
          <h2 className="text-xl font-semibold text-white mb-4">Webhook Request Body</h2>
          <p className="text-white-muted mb-4">
            The following parameters are sent in the webhook request body:
          </p>
          
          <div className="bg-white/5 border border-white/10 rounded-lg overflow-hidden mb-6">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-white/5">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-white-muted uppercase tracking-wider">Parameter</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-white-muted uppercase tracking-wider">Type</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-white-muted uppercase tracking-wider">Required</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-white-muted uppercase tracking-wider">Description</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/10">
                  {webhookRequestParams.map((param, index) => (
                    <tr key={index} className="hover:bg-white/5">
                      <td className="px-4 py-4 whitespace-nowrap text-sm font-mono text-lime">{param.name}</td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm font-mono text-white-muted">{param.type}</td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          param.required 
                            ? 'bg-red-100 text-red-800' 
                            : 'bg-gray-100 text-gray-800'
                        }`}>
                          {param.required ? 'Yes' : 'No'}
                        </span>
                      </td>
                      <td className="px-4 py-4 text-sm text-white-muted">{param.description}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* Webhook Response Example */}
        <div className="mb-8">
          <h2 className="text-xl font-semibold text-white mb-4">Webhook Response Example</h2>
          <p className="text-white-muted mb-4">
            This response object provides detailed information about the transaction, authentication, authorization, and settlement data.
          </p>
          <CodeBlock code={webhookResponseJson} language="json" />
        </div>

        {/* Best Practices */}
        <div className="mb-8">
          <h2 className="text-xl font-semibold text-white mb-4">Best Practices</h2>
          
          <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-6">
            <div className="flex items-center mb-3">
              <CheckCircle className="h-5 w-5 text-green-500 mr-2" />
              <h3 className="text-lg font-semibold text-green-500">Implementation Guidelines</h3>
            </div>
            <ul className="text-sm text-green-500/90 space-y-2">
              <li>• Use HTTPS endpoints for webhook URLs</li>
              <li>• Implement idempotency to handle duplicate events</li>
              <li>• Verify webhook signatures for security</li>
              <li>• Return HTTP 200 status for successful processing</li>
              <li>• Implement proper error handling and logging</li>
              <li>• Store webhook events for audit and reconciliation</li>
              <li>• Handle webhook retries gracefully</li>
              <li>• Process webhooks asynchronously to avoid timeouts</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};